import { notFound, usePathname } from 'next/navigation';

import type { ComponentType} from 'react';
import { useMemo } from 'react';

import ImageProxy from '@next-image-proxy';

import type { StorePageId } from '@api';
import { STORE_PAGE_ID } from '@api';

import { AiAgents } from '@uikit/icons/AiAgents';
import { Flame } from '@uikit/icons/Flame';
import { MultiColorCompass } from '@uikit/icons/MultiColorCompass';
import { MultiColorDownload } from '@uikit/icons/MultiColorDownload';
import { MultiColorGamepad } from '@uikit/icons/MultiColorGamepad';
import { MultiColorGift2 } from '@uikit/icons/MultiColorGift2';
import { MultiColorUpcoming } from '@uikit/icons/MultiColorUpcoming';
import { TelegramMiniApps } from '@uikit/icons/TelegramMiniApps';

import type { BreadcrumbsItem } from '@store:web/components/common/Breadcrumbs/Breadcrumbs';
import type {
  BasePageHeaderProps,
} from '@store:web/components/layout/PageHeader/BasePageHeader';
import BasePageHeader from '@store:web/components/layout/PageHeader/BasePageHeader';
import { roadToTgeQuests } from '@store:web/components/pages/StoryPage/hardcodedStories/roadToTgeQuests';

type PageWithHeaderId =
  | StorePageId
  | 'hot-offers'
  | 'magic-board'
  | 'perks'
  | 'tge'
  | 'user'
  | 'reward-pools';

const pageHeaderProps: Record<
  PageWithHeaderId,
  BasePageHeaderProps & { Icon?: ComponentType<{ className?: string }>; showAdEPOMBanner?: boolean }
> = {
  [STORE_PAGE_ID.discover]: {
    title: 'Discover: Explore the Magic Store Web3 App Store',
    Icon: MultiColorCompass,
    decorImage: {
      mobile: '/page-header/discover-page-banner-decor-mobile.png',
      desktop: '/page-header/discover-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
    ],
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.apps]: {
    title: 'Apps: Start a Web3 Journey - DeFi, CeFi, Wallets, & More',
    Icon: MultiColorDownload,
    decorImage: {
      mobile: '/page-header/apps-page-banner-decor-mobile.png',
      desktop: '/page-header/apps-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Apps',
        pathname: '/apps',
      },
    ],
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.games]: {
    title: 'Games: The Future of Web3 Play - GameFi, Metaverse, NFTs, & More',
    Icon: MultiColorGamepad,
    decorImage: {
      mobile: '/page-header/games-page-banner-decor-mobile.png',
      desktop: '/page-header/games-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Games',
        pathname: '/games',
      },
    ],
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.upcoming]: {
    title: 'Upcoming: Discover Apps and Games in Beta or Development',
    Icon: MultiColorUpcoming,
    decorImage: {
      mobile: '/page-header/upcoming-page-banner-decor-mobile.png',
      desktop: '/page-header/upcoming-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Apps',
        pathname: '/apps',
      },
      {
        label: 'Upcoming',
        pathname: '/upcoming',
      },
    ],
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.aiAgents]: {
    title: 'AI Agents: Revolutionizing Web3 with Intelligent Automation & Beyond',
    Icon: (props) => <AiAgents {...props} className="opacity-40" />,
    decorImage: {
      mobile: '/page-header/apps-page-banner-decor-mobile.png',
      desktop: '/page-header/apps-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'AI Agents',
        pathname: '/ai-agents',
      },
    ],
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.memes]: {
    title: 'Meme Tokens: From Viral Fun to Decentralized Impact',
    Icon: (props) => (
      <ImageProxy
        {...props}
        alt="Meme tokens image"
        src="/page-header/meme-tokens.png"
        width={230}
        height={184}
        format="png"
        className="opacity-90"
      />
    ),
    decorImage: {
      mobile: '/page-header/apps-page-banner-decor-mobile.png',
      desktop: '/page-header/apps-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Memes',
        pathname: '/memes',
      },
    ],
    iconClassName: 'aspect-auto',
    showAdEPOMBanner: true,
  },
  [STORE_PAGE_ID.telegramMiniApps]: {
    title: 'Mini Apps for Telegram: Simplifying Web3 Access with Seamless Integration',
    Icon: (props) => <TelegramMiniApps {...props} className="opacity-40 py-3" />,
    decorImage: {
      mobile: '/page-header/apps-page-banner-decor-mobile.png',
      desktop: '/page-header/apps-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Telegram Mini-Apps',
        pathname: '/telegram-mini-apps',
      },
    ],
    showAdEPOMBanner: true,
  },
  'hot-offers': {
    title: 'Hot Offers: Exclusive Rewards, Giveaways and Deals for the Magic Store Community',
    icon: <Flame />,
    decorImage: {
      mobile: '/page-header/hot-offers-page-banner-decor-mobile.png',
      desktop: '/page-header/hot-offers-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Hot Offers',
        pathname: '/hot-offers',
      },
    ],
    showAdEPOMBanner: true,
  },
  'magic-board': {
    title: 'Exclusive PoW Hot Offers | MagicWall at MagicStore',
    icon: <Flame />,
    decorImage: {
      mobile: '/page-header/hot-offers-page-banner-decor-mobile.png',
      desktop: '/page-header/hot-offers-page-banner-decor-desktop.png',
    },
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Magic Board',
        pathname: '/magic-board',
      },
    ],
    showAdEPOMBanner: true,
  },
  perks: {
    title: 'Perks: Exclusive Web3 Deals Tailored Just for Magic Store Community',
    decorImage: {
      mobile: '/page-header/perks-page-banner-mobile.png',
      desktop: '/page-header/perks-page-banner.png',
    },
    Icon: MultiColorGift2,
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Perks',
        pathname: '/perks',
      },
    ],
  },
  tge: {
    title: 'Magic Square Road to TGE in Collaboration with CoinList',
    decorImage: {
      mobile: '/page-header/tge-page-banner-decor-desktop.png',
      desktop: '/page-header/tge-page-banner-decor-desktop.png',
    },
    icon: (
      <ImageProxy
        alt=""
        src="/page-header/tge-page-banner-icon-desktop.png"
        width={407}
        height={368}
      />
    ),
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Road to TGE Quests',
        pathname: `/stories/${roadToTgeQuests.id}`,
      },
    ],
  },
  'reward-pools': {
    title: 'Reward Pools',
    decorImage: {
      // TODO: update images
      mobile: '/page-header/tge-page-banner-decor-desktop.png',
      desktop: '/page-header/tge-page-banner-decor-desktop.png',
    },
    icon: (
      // TODO: update images
      <ImageProxy
        alt=""
        src="/page-header/tge-page-banner-icon-desktop.png"
        width={407}
        height={368}
      />
    ),
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
      {
        label: 'Reward Pools',
        pathname: `/reward-pools`,
      },
    ],
    showAdEPOMBanner: true,
  },
  user: {
    breadcrumbs: [
      {
        label: 'Discover',
        pathname: '/',
      },
    ],
  },
};

interface PageHeaderProps extends Partial<Omit<BasePageHeaderProps, 'decorImage'>> {
  page: PageWithHeaderId;
  extendBreadcrumbs?: BreadcrumbsItem[];
}

const PageHeader = ({ page, extendBreadcrumbs, ...props }: PageHeaderProps) => {
  const pathname = usePathname();

  if (!pageHeaderProps[page]) {
    notFound();
  }

  const { Icon, breadcrumbs, iconClassName, showAdEPOMBanner, ...headerProps } = pageHeaderProps[page];
  const extendedBreadcrumbs = useMemo(
    () => [...(breadcrumbs || []), ...(extendBreadcrumbs || [])],
    [breadcrumbs, extendBreadcrumbs],
  );

  const hasAdBanner =
    showAdEPOMBanner && (pathname.endsWith(page) || pathname.includes('app/') || pathname === '/');

  return (
    <BasePageHeader
      breadcrumbsCurrentPathname={pathname}
      breadcrumbs={extendedBreadcrumbs}
      icon={Icon && <Icon className="opacity-10" />}
      iconClassName={iconClassName}
      hasAdEPOMBanner={hasAdBanner}
      {...headerProps}
      {...props}
    />
  );
};

export default PageHeader;
